import {
  businessDayBounds,
  formatRatePercent,
  formatTimestamp,
  formatToman,
  statusLabel,
  statusValues,
  toPersianDigits,
  type LocalDate,
} from '@saay/formatting';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { AdminWalletTopUp, AdminWalletTransfer, AdminWalletWithdrawal } from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import {
  AmountFilterPill,
  DateFilterPill,
  FilterPill,
  FilterPillBar,
} from '../components/filter-pills.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import {
  DetailGrid,
  CopyableIdentifier,
  Dialog,
  EmptyState,
  LoadMore,
  Masked,
  Money,
  PageHeader,
  Section,
  Skeleton,
  StatusChip,
  Tabs,
} from '../kit.js';
import {
  useAdminWalletSummary,
  useAdminWalletTopUps,
  useAdminWalletTransactions,
  useAdminWalletTransfers,
  useAdminWalletWithdrawals,
  type WalletTopUpFilters,
  type WalletTransactionFilters,
  type WalletTransferFilters,
  type WalletWithdrawalFilters,
} from '../queries.js';
import {
  TimelineDetail,
  TimelineTable,
  transactionView,
  WalletBuyer,
  withdrawalStatusLabel,
  type TimelineView,
} from './wallet-timeline.js';

type WalletTab = 'summary' | 'transactions' | 'top-ups' | 'transfers' | 'withdrawals';

const WITHDRAWAL_STATUSES = [
  'PENDING',
  'PROCESSING',
  'SUCCEEDED',
  'FAILED',
  'REVERSED',
  'CANCELLED',
] as const satisfies readonly AdminWalletWithdrawal['status'][];

const WALLET_KINDS = [
  'TOP_UP',
  'WITHDRAWAL',
  'TRANSFER_SENT',
  'REFUND',
  'WALLET_FREEZE',
  'WALLET_RELEASE',
  'INSTALLMENT_PAYMENT',
  'PURCHASE',
] as const;

const NEWEST_SORT_OPTION = [{ label: 'جدیدترین', value: '' }] as const;

function tabOf(pathname: string): WalletTab {
  if (pathname.endsWith('/transactions')) return 'transactions';
  if (pathname.endsWith('/top-ups')) return 'top-ups';
  if (pathname.endsWith('/transfers')) return 'transfers';
  if (pathname.endsWith('/withdrawals')) return 'withdrawals';
  return 'summary';
}

function dateBound(value: string, edge: 'start' | 'end'): string | undefined {
  return value === '' ? undefined : businessDayBounds(value)[edge];
}

function amountBound(value: number | null): number | undefined {
  return value === null ? undefined : value;
}

function SummaryPanel() {
  const summary = useAdminWalletSummary();
  const data = summary.data;
  return (
    <QueryState
      empty={<EmptyState title="هنوز کیف پولی ساخته نشده است." />}
      error={summary.error}
      isEmpty={data?.walletCount === 0}
      onRetry={() => void summary.refetch()}
      skeleton={<Skeleton label="در حال بارگذاری خلاصه کیف پول…" />}
      status={summary.status}
    >
      {data === undefined ? null : (
        <Section id="wallet-summary" title="وضعیت کیف پول‌ها">
          <DetailGrid
            items={[
              { key: 'count', label: 'تعداد کیف پول', value: toPersianDigits(data.walletCount) },
              {
                key: 'free',
                label: 'موجودی آزاد',
                value: <Money rials={data.totalFreeBalanceIrr} />,
              },
              {
                key: 'held',
                label: 'در انتظار تأیید خرید',
                value: <Money rials={data.totalHeldBalanceIrr} />,
              },
              {
                key: 'frozen',
                label: 'مبلغ بلوکه‌شده',
                value: <Money rials={data.totalFrozenBalanceIrr} />,
              },
              {
                key: 'pending',
                label: 'در حال برداشت',
                value: <Money rials={data.totalWithdrawalPendingIrr} />,
              },
              {
                key: 'credit',
                label: 'اعتبار از کیف پول',
                value: <Money rials={data.walletCreditAvailableIrr} />,
              },
              {
                key: 'outstanding',
                label: 'مانده اعتبار کیف پول',
                value: <Money rials={data.walletCreditOutstandingIrr} />,
              },
              {
                key: 'overrides',
                label: 'خریداران با ضریب اختصاصی',
                value: toPersianDigits(data.buyersWithMultiplierOverride),
              },
              {
                key: 'collected',
                label: 'وصول ۳۰ روز',
                value: <Money rials={data.collectedLast30DaysIrr} />,
              },
              {
                key: 'open',
                label: 'برداشت‌های باز',
                value: toPersianDigits(data.openWithdrawalCount),
              },
              {
                key: 'stuck',
                label: 'برداشت‌های گیرکرده',
                value: toPersianDigits(data.stuckWithdrawalCount),
              },
            ]}
          />
        </Section>
      )}
    </QueryState>
  );
}

function TransactionFilters({
  amountFrom,
  amountTo,
  onAmountChange,
  params,
  setParams,
}: {
  readonly amountFrom: number | null;
  readonly amountTo: number | null;
  readonly onAmountChange: (from: number | null, to: number | null) => void;
  readonly params: URLSearchParams;
  readonly setParams: (next: URLSearchParams) => void;
}) {
  const kind = params.get('kind') ?? '';
  const status = params.get('status') ?? '';
  const paymentType = params.get('paymentType') ?? '';
  const paymentSource = params.get('paymentSource') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  function set(name: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setParams(next);
  }
  function setDateRange(nextFrom: LocalDate, nextTo: LocalDate) {
    const next = new URLSearchParams(params);
    if (nextFrom === '') next.delete('from');
    else next.set('from', nextFrom);
    if (nextTo === '') next.delete('to');
    else next.set('to', nextTo);
    setParams(next);
  }
  return (
    <FilterPillBar
      activeFilterCount={
        [kind, status, paymentType, paymentSource, params.get('buyerId') ?? ''].filter(Boolean)
          .length +
        (from === '' && to === '' ? 0 : 1) +
        (amountFrom === null && amountTo === null ? 0 : 1)
      }
      label="فیلتر تراکنش‌های کیف پول"
      onClearAll={() => {
        const next = new URLSearchParams(params);
        for (const key of [
          'kind',
          'status',
          'paymentType',
          'paymentSource',
          'buyerId',
          'from',
          'to',
        ]) {
          next.delete(key);
        }
        setParams(next);
        onAmountChange(null, null);
      }}
      sort={
        <FilterPill
          inactiveLabel="جدیدترین"
          label="مرتب‌سازی"
          onChange={() => undefined}
          options={NEWEST_SORT_OPTION}
          value=""
        />
      }
    >
      <FilterPill
        emptyLabel="همه نوع‌ها"
        label="نوع"
        onChange={(value) => set('kind', value)}
        options={WALLET_KINDS.map((value) => ({
          label: statusLabel('moneyTimelineKind', value),
          value,
        }))}
        value={kind}
      />
      <FilterPill
        emptyLabel="همه وضعیت‌ها"
        label="وضعیت"
        onChange={(value) => set('status', value)}
        options={[
          ...statusValues('walletTopUpStatus').map((value) => ({
            label: statusLabel('walletTopUpStatus', value),
            value,
          })),
          { label: 'ثبت‌شده', value: 'POSTED' },
          { label: 'واریز به کیف پول', value: 'CREDITED_TO_WALLET' },
        ]}
        value={status}
      />
      <FilterPill
        emptyLabel="همه نوع‌های خرید"
        label="نوع خرید"
        onChange={(value) => set('paymentType', value)}
        options={statusValues('salePaymentType').map((value) => ({
          label: statusLabel('salePaymentType', value),
          value,
        }))}
        value={paymentType}
      />
      <FilterPill
        emptyLabel="همه منابع پرداخت"
        label="منبع پرداخت"
        onChange={(value) => set('paymentSource', value)}
        options={statusValues('moneyTimelinePaymentSource').map((value) => ({
          label: statusLabel('moneyTimelinePaymentSource', value),
          value,
        }))}
        value={paymentSource}
      />
      <DateFilterPill
        from={from as LocalDate}
        namePrefix="transactions"
        onApply={setDateRange}
        to={to as LocalDate}
      />
      <AmountFilterPill from={amountFrom} onApply={onAmountChange} to={amountTo} />
    </FilterPillBar>
  );
}

function transactionFilters(
  params: URLSearchParams,
  amounts: { from: number | null; to: number | null },
): WalletTransactionFilters {
  const paymentType = params.get('paymentType');
  const occurredFrom = dateBound(params.get('from') ?? '', 'start');
  const occurredTo = dateBound(params.get('to') ?? '', 'end');
  const amountFrom = amountBound(amounts.from);
  const amountTo = amountBound(amounts.to);
  return {
    ...(params.get('kind') === null ? {} : { kind: params.get('kind') ?? '' }),
    ...(params.get('status') === null ? {} : { status: params.get('status') ?? '' }),
    ...(params.get('buyerId') === null ? {} : { buyerId: params.get('buyerId') ?? '' }),
    ...(params.get('paymentSource') === null
      ? {}
      : { paymentSource: params.get('paymentSource') ?? '' }),
    ...(paymentType === 'INSTALLMENT' || paymentType === 'WALLET_CASH' ? { paymentType } : {}),
    ...(occurredFrom === undefined ? {} : { occurredFrom }),
    ...(occurredTo === undefined ? {} : { occurredTo }),
    ...(amountFrom === undefined ? {} : { amountFrom }),
    ...(amountTo === undefined ? {} : { amountTo }),
    sort: '-occurredAt',
  };
}

function TransactionsPanel() {
  const [params, setParams] = useSearchParams();
  const [amountFrom, setAmountFrom] = useState<number | null>(null);
  const [amountTo, setAmountTo] = useState<number | null>(null);
  const [open, setOpen] = useState<TimelineView | null>(null);
  const list = useAdminWalletTransactions(
    transactionFilters(params, { from: amountFrom, to: amountTo }),
  );
  const rows = (list.data?.pages.flatMap((page) => page.items) ?? []).map(transactionView);
  return (
    <Section id="wallet-transactions" title="تراکنش‌های کیف پول">
      <TransactionFilters
        amountFrom={amountFrom}
        amountTo={amountTo}
        onAmountChange={(nextFrom, nextTo) => {
          setAmountFrom(nextFrom);
          setAmountTo(nextTo);
        }}
        params={params}
        setParams={setParams}
      />
      <QueryState
        empty={<EmptyState title="تراکنشی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری تراکنش‌های کیف پول…" />}
        status={list.status}
      >
        <TimelineTable caption="تراکنش‌های کیف پول" onOpen={setOpen} rows={rows} showBuyer />
        <LoadMore
          hasMore={list.hasNextPage}
          loading={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </QueryState>
      <TimelineDetail onClose={() => setOpen(null)} row={open} />
    </Section>
  );
}

function TopUpsPanel() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const buyerId = params.get('buyerId') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const createdFrom = dateBound(from, 'start');
  const createdTo = dateBound(to, 'end');
  const knownStatus = statusValues('walletTopUpStatus').find((value) => value === status);
  const filters: WalletTopUpFilters = {
    ...(knownStatus === undefined ? {} : { status: knownStatus }),
    ...(buyerId === '' ? {} : { buyerId }),
    ...(createdFrom === undefined ? {} : { createdFrom }),
    ...(createdTo === undefined ? {} : { createdTo }),
  };
  const list = useAdminWalletTopUps(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const [open, setOpen] = useState<AdminWalletTopUp | null>(null);
  const columns: readonly DataTableColumn<AdminWalletTopUp>[] = [
    {
      header: 'خریدار',
      id: 'buyer',
      pin: 'start',
      render: (row) => <WalletBuyer buyer={row.buyer} />,
    },
    {
      header: 'مبلغ',
      id: 'amount',
      numeric: true,
      render: (row) => <Money rials={row.amountIrr} />,
    },
    { header: 'کارمزد', id: 'fee', numeric: true, render: (row) => <Money rials={row.feeIrr} /> },
    {
      header: 'پرداختی',
      id: 'total',
      numeric: true,
      render: (row) => <Money rials={row.totalIrr} />,
    },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => <StatusChip enumName="walletTopUpStatus" value={row.status} />,
    },
    { header: 'زمان', id: 'when', render: (row) => formatTimestamp(row.createdAt) },
  ];
  function set(name: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setParams(next);
  }
  function setDateRange(nextFrom: LocalDate, nextTo: LocalDate) {
    const next = new URLSearchParams(params);
    if (nextFrom === '') next.delete('from');
    else next.set('from', nextFrom);
    if (nextTo === '') next.delete('to');
    else next.set('to', nextTo);
    setParams(next);
  }
  return (
    <Section id="wallet-top-ups" title="شارژها">
      <FilterPillBar
        activeFilterCount={
          [status, buyerId].filter(Boolean).length + (from === '' && to === '' ? 0 : 1)
        }
        label="فیلتر شارژها"
        onClearAll={() => {
          const next = new URLSearchParams(params);
          for (const key of ['status', 'buyerId', 'from', 'to']) next.delete(key);
          setParams(next);
        }}
        sort={
          <FilterPill
            inactiveLabel="جدیدترین"
            label="مرتب‌سازی"
            onChange={() => undefined}
            options={NEWEST_SORT_OPTION}
            value=""
          />
        }
      >
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت"
          onChange={(value) => set('status', value)}
          options={statusValues('walletTopUpStatus').map((value) => ({
            label: statusLabel('walletTopUpStatus', value),
            value,
          }))}
          value={status}
        />
        <DateFilterPill
          from={from as LocalDate}
          namePrefix="top-up"
          onApply={setDateRange}
          to={to as LocalDate}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState title="شارژی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری شارژها…" />}
        status={list.status}
      >
        <AdminListTable
          caption="شارژهای کیف پول"
          columns={columns}
          mobileSummary={(row) => ({
            primary: formatToman(row.amountIrr),
            secondary: statusLabel('walletTopUpStatus', row.status),
          })}
          onRowClick={setOpen}
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={list.hasNextPage}
          loading={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </QueryState>
      <Dialog
        onOpenChange={(next) => {
          if (!next) setOpen(null);
        }}
        open={open !== null}
        title="جزئیات شارژ"
      >
        {open === null ? null : (
          <DetailGrid
            items={[
              { key: 'buyer', label: 'خریدار', value: <WalletBuyer buyer={open.buyer} /> },
              { key: 'amount', label: 'مبلغ', value: <Money rials={open.amountIrr} /> },
              { key: 'fee', label: 'کارمزد', value: <Money rials={open.feeIrr} /> },
              {
                key: 'rate',
                label: 'نرخ کارمزد',
                value: formatRatePercent(open.feeRate, { invalid: 'raw' }),
              },
              {
                key: 'minimum',
                label: 'حداقل کارمزد',
                value: <Money rials={open.feeMinimumIrr} />,
              },
              { key: 'total', label: 'مبلغ پرداختی', value: <Money rials={open.totalIrr} /> },
              {
                key: 'provider',
                label: 'درگاه',
                value:
                  open.provider === null ? 'ندارد' : statusLabel('walletProvider', open.provider),
              },
              {
                key: 'reference',
                label: 'شماره پیگیری درگاه',
                value:
                  open.providerReference === null ? (
                    'ندارد'
                  ) : (
                    <CopyableIdentifier label={null} value={open.providerReference} />
                  ),
              },
              {
                key: 'reconciliation',
                label: 'مغایرت',
                value:
                  open.reconciliationStatus === null
                    ? 'ندارد'
                    : statusLabel('walletReconciliationStatus', open.reconciliationStatus),
              },
              {
                key: 'withdrawable',
                label: 'قابل برداشت از',
                value:
                  open.withdrawableFrom === null ? '—' : formatTimestamp(open.withdrawableFrom),
              },
            ]}
          />
        )}
      </Dialog>
    </Section>
  );
}

function stepUpLabel(method: AdminWalletTransfer['stepUpMethod']): string {
  switch (method) {
    case 'APP_PIN':
      return 'رمز برنامه';
    case 'BIOMETRIC':
      return 'اثر انگشت یا چهره';
    case 'WEBAUTHN':
      return 'کلید عبور';
  }
}

function WithdrawalsPanel() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const buyerId = params.get('buyerId') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const requestedFrom = dateBound(from, 'start');
  const requestedTo = dateBound(to, 'end');
  const knownStatus = WITHDRAWAL_STATUSES.find((value) => value === status);
  const filters: WalletWithdrawalFilters = {
    ...(knownStatus === undefined ? {} : { status: knownStatus }),
    ...(buyerId === '' ? {} : { buyerId }),
    ...(requestedFrom === undefined ? {} : { requestedFrom }),
    ...(requestedTo === undefined ? {} : { requestedTo }),
  };
  const list = useAdminWalletWithdrawals(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const [open, setOpen] = useState<AdminWalletWithdrawal | null>(null);
  const columns: readonly DataTableColumn<AdminWalletWithdrawal>[] = [
    {
      header: 'خریدار',
      id: 'buyer',
      pin: 'start',
      render: (row) => <WalletBuyer buyer={row.buyer} />,
    },
    {
      header: 'مبلغ',
      id: 'amount',
      numeric: true,
      render: (row) => <Money rials={row.amountIrr} />,
    },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => withdrawalStatusLabel(row.status),
    },
    { header: 'زمان', id: 'when', render: (row) => formatTimestamp(row.requestedAt) },
  ];
  function set(name: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setParams(next);
  }
  function setDateRange(nextFrom: LocalDate, nextTo: LocalDate) {
    const next = new URLSearchParams(params);
    if (nextFrom === '') next.delete('from');
    else next.set('from', nextFrom);
    if (nextTo === '') next.delete('to');
    else next.set('to', nextTo);
    setParams(next);
  }
  return (
    <Section id="wallet-withdrawals" title="برداشت‌ها">
      <FilterPillBar
        activeFilterCount={
          [status, buyerId].filter(Boolean).length + (from === '' && to === '' ? 0 : 1)
        }
        label="فیلتر برداشت‌ها"
        onClearAll={() => {
          const next = new URLSearchParams(params);
          for (const key of ['status', 'buyerId', 'from', 'to']) next.delete(key);
          setParams(next);
        }}
        sort={
          <FilterPill
            inactiveLabel="جدیدترین"
            label="مرتب‌سازی"
            onChange={() => undefined}
            options={NEWEST_SORT_OPTION}
            value=""
          />
        }
      >
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت"
          onChange={(value) => set('status', value)}
          options={WITHDRAWAL_STATUSES.map((value) => ({
            label: withdrawalStatusLabel(value),
            value,
          }))}
          value={status}
        />
        <DateFilterPill
          from={from as LocalDate}
          namePrefix="withdrawal"
          onApply={setDateRange}
          to={to as LocalDate}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState title="برداشتی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری برداشت‌ها…" />}
        status={list.status}
      >
        <AdminListTable
          caption="برداشت‌های کیف پول"
          columns={columns}
          mobileSummary={(row) => ({
            primary: formatToman(row.amountIrr),
            secondary: withdrawalStatusLabel(row.status),
          })}
          onRowClick={setOpen}
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={list.hasNextPage}
          loading={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </QueryState>
      <Dialog
        onOpenChange={(next) => {
          if (!next) setOpen(null);
        }}
        open={open !== null}
        title="جزئیات برداشت"
      >
        {open === null ? null : (
          <DetailGrid
            items={[
              { key: 'buyer', label: 'خریدار', value: <WalletBuyer buyer={open.buyer} /> },
              { key: 'amount', label: 'مبلغ', value: <Money rials={open.amountIrr} /> },
              { key: 'status', label: 'وضعیت', value: withdrawalStatusLabel(open.status) },
              { key: 'holder', label: 'صاحب حساب', value: open.payoutAccount.holderName },
              {
                key: 'sheba',
                label: 'شبا',
                value: <Masked kind="sheba" value={open.payoutAccount.shebaMasked} />,
              },
              { key: 'step', label: 'روش تأیید', value: stepUpLabel(open.stepUpMethod) },
              { key: 'when', label: 'زمان درخواست', value: formatTimestamp(open.requestedAt) },
              {
                key: 'bank',
                label: 'پیگیری بانک',
                value:
                  open.bankReference === null ? (
                    'ندارد'
                  ) : (
                    <CopyableIdentifier label={null} value={open.bankReference} />
                  ),
              },
              {
                key: 'provider',
                label: 'درگاه',
                value:
                  open.provider === null ? 'ندارد' : statusLabel('walletProvider', open.provider),
              },
              {
                key: 'reconciliation',
                label: 'مغایرت',
                value:
                  open.reconciliationStatus === null
                    ? 'ندارد'
                    : statusLabel('walletReconciliationStatus', open.reconciliationStatus),
              },
            ]}
          />
        )}
      </Dialog>
    </Section>
  );
}

/** WL28: every transfer, or the large-transfer view (`sort=-amountIrr` and the admin's amount floor). */
function TransfersPanel() {
  const [params, setParams] = useSearchParams();
  const [amountFrom, setAmountFrom] = useState<number | null>(null);
  const [amountTo, setAmountTo] = useState<number | null>(null);
  const large = params.get('view') === 'large';
  const buyerId = params.get('buyerId') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const createdFrom = dateBound(from, 'start');
  const createdTo = dateBound(to, 'end');
  const minAmount = amountBound(amountFrom);
  const maxAmount = amountBound(amountTo);
  const sortParam = params.get('sort') === '-amountIrr' ? '-amountIrr' : '-createdAt';
  const sort = large ? '-amountIrr' : sortParam;
  const filters: WalletTransferFilters = {
    sort,
    ...(buyerId === '' ? {} : { buyerId }),
    ...(createdFrom === undefined ? {} : { createdFrom }),
    ...(createdTo === undefined ? {} : { createdTo }),
    ...(minAmount === undefined ? {} : { amountFrom: minAmount }),
    ...(maxAmount === undefined ? {} : { amountTo: maxAmount }),
  };
  const list = useAdminWalletTransfers(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const [open, setOpen] = useState<AdminWalletTransfer | null>(null);
  const title = large ? 'انتقال‌های بزرگ' : 'انتقال‌های کیف پول';
  const columns: readonly DataTableColumn<AdminWalletTransfer>[] = [
    {
      header: 'فرستنده',
      id: 'sender',
      pin: 'start',
      render: (row) => <WalletBuyer buyer={row.sender} />,
    },
    {
      header: 'گیرنده',
      id: 'recipient',
      render: (row) => <WalletBuyer buyer={row.recipient} />,
    },
    {
      header: 'مبلغ',
      id: 'amount',
      numeric: true,
      render: (row) => <Money rials={row.amountIrr} />,
    },
    { header: 'زمان', id: 'when', render: (row) => formatTimestamp(row.completedAt) },
  ];
  function set(name: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setParams(next);
  }
  function setDateRange(nextFrom: LocalDate, nextTo: LocalDate) {
    const next = new URLSearchParams(params);
    if (nextFrom === '') next.delete('from');
    else next.set('from', nextFrom);
    if (nextTo === '') next.delete('to');
    else next.set('to', nextTo);
    setParams(next);
  }
  return (
    <Section id="wallet-transfers" title={large ? 'انتقال‌های بزرگ' : 'انتقال‌ها'}>
      <FilterPillBar
        activeFilterCount={
          [large ? 'large' : '', buyerId, large ? '' : (params.get('sort') ?? '')].filter(Boolean)
            .length +
          (from === '' && to === '' ? 0 : 1) +
          (amountFrom === null && amountTo === null ? 0 : 1)
        }
        label="فیلتر انتقال‌ها"
        onClearAll={() => {
          const next = new URLSearchParams(params);
          for (const key of ['view', 'sort', 'buyerId', 'from', 'to']) next.delete(key);
          setParams(next);
          setAmountFrom(null);
          setAmountTo(null);
        }}
        sort={
          <FilterPill
            disabled={large}
            inactiveLabel={large ? 'بزرگ‌ترین مبلغ' : 'جدیدترین'}
            label="مرتب‌سازی"
            onChange={(value) => set('sort', value)}
            options={[{ label: 'بزرگ‌ترین مبلغ', value: '-amountIrr' }]}
            value={!large && sortParam === '-amountIrr' ? '-amountIrr' : ''}
          />
        }
      >
        <FilterPill
          emptyLabel="همه انتقال‌ها"
          label="نوع"
          onChange={(value) => set('view', value)}
          options={[{ label: 'انتقال‌های بزرگ', value: 'large' }]}
          value={large ? 'large' : ''}
        />
        <DateFilterPill
          from={from as LocalDate}
          namePrefix="transfer"
          onApply={setDateRange}
          to={to as LocalDate}
        />
        <AmountFilterPill
          from={amountFrom}
          onApply={(nextFrom, nextTo) => {
            setAmountFrom(nextFrom);
            setAmountTo(nextTo);
          }}
          to={amountTo}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState title="انتقالی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری انتقال‌ها…" />}
        status={list.status}
      >
        <AdminListTable
          caption={title}
          columns={columns}
          mobileSummary={(row) => ({
            primary: formatToman(row.amountIrr),
            secondary: row.sender.displayName,
          })}
          onRowClick={setOpen}
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={list.hasNextPage}
          loading={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </QueryState>
      <Dialog
        onOpenChange={(next) => {
          if (!next) setOpen(null);
        }}
        open={open !== null}
        title="جزئیات انتقال"
      >
        {open === null ? null : (
          <DetailGrid
            items={[
              {
                key: 'sender',
                label: 'فرستنده',
                value: <WalletBuyer buyer={open.sender} />,
              },
              {
                key: 'recipient',
                label: 'گیرنده',
                value: <WalletBuyer buyer={open.recipient} />,
              },
              { key: 'amount', label: 'مبلغ', value: <Money rials={open.amountIrr} /> },
              { key: 'status', label: 'وضعیت', value: 'انجام‌شده' },
              { key: 'note', label: 'بابت', value: open.noteFa ?? '—' },
              { key: 'step', label: 'روش تأیید', value: stepUpLabel(open.stepUpMethod) },
              { key: 'when', label: 'زمان', value: formatTimestamp(open.completedAt) },
              {
                key: 'withdrawable',
                label: 'قابل برداشت برای گیرنده',
                value: formatTimestamp(open.recipientWithdrawableFrom),
              },
            ]}
          />
        )}
      </Dialog>
    </Section>
  );
}

/** Wallet operations (AC §30.15, §30.26, §30.28). */
export function WalletsPage() {
  usePageTitle('کیف پول');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab = tabOf(pathname);
  return (
    <RequireArea area="wallets">
      {() => (
        <>
          <PageHeader title="کیف پول" />
          <Tabs
            label="بخش‌های کیف پول"
            onChange={(next) => {
              void navigate(next === 'summary' ? '/wallets' : `/wallets/${next}`);
            }}
            panel={
              tab === 'transactions' ? (
                <TransactionsPanel />
              ) : tab === 'top-ups' ? (
                <TopUpsPanel />
              ) : tab === 'withdrawals' ? (
                <WithdrawalsPanel />
              ) : tab === 'transfers' ? (
                <TransfersPanel />
              ) : (
                <SummaryPanel />
              )
            }
            tabs={[
              { key: 'summary', label: 'خلاصه' },
              { key: 'transactions', label: 'تراکنش‌ها' },
              { key: 'top-ups', label: 'شارژها' },
              { key: 'withdrawals', label: 'برداشت‌ها' },
              { key: 'transfers', label: 'انتقال‌ها' },
            ]}
            value={tab}
          />
        </>
      )}
    </RequireArea>
  );
}
